import { useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { useIsSentinelUser } from '../../../authUserProvider'
import styles from './styles.module.scss'

export interface AdminReportOption {
    // Backend reportType passed to POST /download-report.
    value: string
    // Dropdown label.
    label: string
    // Download filename slug (appended after `filenamePrefix`, before the date).
    filename: string
}

interface Props {
    // The set of reports selectable from the dropdown.
    reportOptions: AdminReportOption[]
    // Slug prepended to every download filename, e.g. 'sentinel-matters'.
    filenamePrefix: string
    title?: string
    subtitle?: string
}

// Admin-only "Download a Report" section, mirroring the Evans Hughes (MFC)
// reports block but parameterised by introducer. Hidden entirely from
// non-Sentinel users (the backend independently gates each reportType too).
export const AdminReportsSection = ({
    reportOptions,
    filenamePrefix,
    title = 'Download a Report',
    subtitle
}: Props) => {
    const isSentinelUser = useIsSentinelUser()
    const [selectedReport, setSelectedReport] = useState<string>(reportOptions[0]?.value ?? '')
    const [reportLoading, setReportLoading] = useState(false)

    // Fetch the generated CSV and trigger a blob download via an anchor element
    // rather than window.open — iOS Safari blocks window.open() after an await
    // (no longer a direct user gesture). A same-origin blob URL works reliably.
    const triggerBlobDownload = async (fileUrl: string, filename: string) => {
        const csvText = await fetch(fileUrl).then((r) => r.text())
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' })
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(blobUrl)
    }

    const downloadSelectedReport = async () => {
        const option = reportOptions.find((o) => o.value === selectedReport)
        if (!option) return

        setReportLoading(true)
        Loading.standard('Generating report...')

        try {
            const response = await api({
                url: '/download-report',
                data: { reportType: option.value, isFullReport: true }
            })
            const fileUrl = response.data?.fileUrl
            if (!fileUrl) {
                Report.failure('Error', 'No file URL received from server', 'OK')
                return
            }

            const filename = `${filenamePrefix}-${option.filename}-${new Date().toISOString().slice(0, 10)}.csv`
            await triggerBlobDownload(fileUrl, filename)

            Report.success('Success', 'Report generated successfully!', 'OK')
        } catch (error: any) {
            console.error('Error downloading report:', error)
            Report.failure('Error', error?.response?.data?.message || 'Failed to generate report. Please try again.', 'OK')
        } finally {
            setReportLoading(false)
            Loading.remove()
        }
    }

    // Admin-only feature: hide the entire section from non-Sentinel users.
    if (!isSentinelUser) return null
    if (!reportOptions.length) return null

    return (
        <section className={styles.reportSection}>
            <h2 className={styles.sectionTitle}>{title}</h2>
            {subtitle && <p className={styles.helpText}>{subtitle}</p>}
            <div className={styles.weekRow}>
                <select
                    className={styles.weekSelect}
                    value={selectedReport}
                    onChange={(e) => setSelectedReport(e.target.value)}
                    disabled={reportLoading}
                >
                    {reportOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <button className={styles.downloadButton} onClick={downloadSelectedReport} disabled={reportLoading}>
                    {reportLoading ? 'Generating...' : 'Download'}
                </button>
            </div>
        </section>
    )
}

export default AdminReportsSection
